import type { ReactNode } from "react";
import { Breadcrumb, type BreadcrumbProps, Flex, Grid } from "antd";

import { CompanyLogo } from "@av/components/molecules/logo/CompanyLogo";
import { AppSwitcher } from "../AppSwitcher";
import { type SegmentName, useNavSegments } from "../hooks/useNavSegments";

import type { AppSwitcherEntry, Company } from "../../type";
import type { NavItem } from "../types";

/**
 * Segments to leave out of the breadcrumb, keyed by the primary nav item key.
 * The primary segment cannot be ignored because it is the default home of each page in the app.
 */
export type IgnoredBreadcrumbSegments = {
  [key: string]: Exclude<SegmentName, "primarySegment">[];
};

interface HeaderBreadcrumbProps {
  subTab?: string;
  hideBreadcrumb?: boolean; // Optional prop to control breadcrumb visibility
  secondaryBreadcrumbLabel?: ReactNode;
  ignoredSegmentsInBreadcrumb?: IgnoredBreadcrumbSegments;
  apps: AppSwitcherEntry[];
  company?: Company;
  currentApp: string;
  onAppChange: (path: string) => void;
  navItems: NavItem[];
  items?: BreadcrumbProps["items"];
}

export function HeaderBreadcrumb({
  hideBreadcrumb,
  apps,
  company,
  currentApp,
  onAppChange,
  navItems,
  secondaryBreadcrumbLabel,
  ignoredSegmentsInBreadcrumb,
  items: customItems,
}: HeaderBreadcrumbProps) {
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  const { primarySegment, secondarySegment, primaryNavItem, secondaryNavItem, tertiaryNavItem } =
    useNavSegments(navItems);
  const ignoredSegments = ignoredSegmentsInBreadcrumb?.[primaryNavItem?.key ?? ""] ?? [];

  const navItemsBreadcrumb = [
    {
      title: (
        <span data-testid="header-breadcrumb-primary">
          {primaryNavItem?.label ?? primarySegment}
        </span>
      ),
    },
  ];

  if (
    (secondaryBreadcrumbLabel || secondaryNavItem) &&
    !ignoredSegments.includes("secondarySegment")
  ) {
    navItemsBreadcrumb.push({
      title: (
        <span data-testid="header-breadcrumb-secondary">
          {secondaryBreadcrumbLabel ?? secondaryNavItem?.label ?? secondarySegment}
        </span>
      ),
    });
  }

  if (tertiaryNavItem && !ignoredSegments.includes("tertiarySegment")) {
    navItemsBreadcrumb.push({
      title: <span data-testid="header-breadcrumb-tertiary">{tertiaryNavItem.label}</span>,
    });
  }

  const items = customItems ?? navItemsBreadcrumb;

  return (
    <Flex align="center" gap={isMobile ? "8px" : "20px"}>
      <CompanyLogo size="small" testIdPrefix="header-breadcrumb" company={company} />
      <AppSwitcher apps={apps} currentApp={currentApp} onAppChange={onAppChange} />
      {!hideBreadcrumb && <Breadcrumb data-testid="header-breadcrumb-text" items={items} />}
    </Flex>
  );
}
